import { useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, Legend, Cell } from 'recharts';
import { AnswersProps, QuestionProps } from '.';
import { FiMail, FiArrowRight, FiCheckCircle, FiAlertCircle, FiInfo } from 'react-icons/fi';
import { Questions } from './questions';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { BsWhatsapp } from "react-icons/bs";
import { HiOutlineDocumentReport, HiOutlineClipboardCheck, HiOutlineLightBulb } from "react-icons/hi";

// Logo Component matching shvesawebsite branding
const Logo: React.FC<{ size?: 'sm' | 'md' | 'lg' }> = ({ size = 'md' }) => {
    const sizes = {
        sm: { icon: 'w-5 h-5', text: 'text-base' },
        md: { icon: 'w-6 h-6', text: 'text-lg' },
        lg: { icon: 'w-7 h-7', text: 'text-xl' }
    };

    return (
        <div className="flex items-center gap-1">
            <svg className={`${sizes[size].icon} text-teal-600`} viewBox="0 0 24 24" fill="currentColor">
                <path d="M17,8C8,10 5.9,16.17 3.82,21.34L5.71,22L6.66,19.7C7.14,19.87 7.64,20 8,20C19,20 22,3 22,3C21,5 14,5.25 9,6.25C4,7.25 2,11.5 2,13.5C2,15.5 3.75,17.25 3.75,17.25C7,8 17,8 17,8Z"/>
            </svg>
            <span className={`${sizes[size].text} font-bold text-slate-800`}>
                Shvesa <span className="font-light text-teal-600">Minds</span>
            </span>
        </div>
    );
};


interface Category { 
    name: string; 
    totalPoints: number; 
    questionCount: number; 
} 

interface ClusteredBarProps {
    name: string;
    totalPoints: number;
    scaledQuestionCount: number;
}  
  
interface CategoryScores { 
    [key: string]: Category; 
} 

type DeviationCategoryData = { 
    name: string; 
    positive: number; 
    negative: number; 
}; 

type QuestionResponse = {
    question_text: string;
    weight: number;
    type: string;
    question_id: string;
    response: string;
};

type ApiResponse = {
    [key: string]: QuestionResponse;
};

const SurveyChart = () => {
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const rawEmail = queryParams.get('email');
    const email = rawEmail ? rawEmail.trim() : null;

    const [error, setError] = useState('')
    const [loading, setLoading] = useState(true)
    const [questions, setQuestions] = useState<QuestionProps[]>([])
    const [answers, setAnswers] = useState<AnswersProps>({})
    const [categoryScores, setCategoryScores] = useState<CategoryScores>({}); 
    const [deviationData, setDeviationData] = useState<DeviationCategoryData[]>([]);
    const [clusteredBarData, setClusteredBarData] = useState<ClusteredBarProps[]>([])

    useEffect(() => {
        const fetchQuestions = async () => {
            if (!email) {
                setError('No email provided');
                setLoading(false);
                return;
            }

            try {
                const url = `https://e6w2t1d2f1.execute-api.us-east-1.amazonaws.com/dev/survey/${email}`;
                const response = await axios.get(url);

                // Check if we have survey data
                if (response.data && response.data.responses && response.data.responses.length > 0) {
                    const surveyData = response.data.responses[0];

                    // Transform the current API response format
                    const formatedAnswer = transformCurrentApiToAnswers(surveyData.responses);
                    setAnswers(formatedAnswer);
                    setQuestions(Questions);
                } else {
                    throw new Error('No survey data found');
                }
            } catch (error:any) {
                setLoading(false)
                setError(error.response?.data?.message || error.message || 'Failed to load survey data');
                console.error('Error fetching questions:', error);
            }
        };

        fetchQuestions();
    }, [email]); 

    useEffect(() => { 
        if (questions && questions.length > 0 && answers) { 
            calculateCategoryScores(); 
            calculateDeviationChart(); 
            setLoading(false)
        } 
    }, [answers, questions]);

    useEffect(() => {
        if (Object.keys(categoryScores).length > 0) {
            transformData(categoryScores);
        }
    }, [categoryScores]);

    // Transform current API response format (array of question responses)
    const transformCurrentApiToAnswers = (apiResponseArray: any[]): { [questionId: string]: string } => {
        const result: { [questionId: string]: string } = {};
        apiResponseArray.forEach((item) => {
            if (item.questionId && item.selectedAnswer) {
                result[item.questionId.toString()] = item.selectedAnswer;
            }
        });
        return result;
    };

    // Legacy transformation function (keeping for compatibility)
    const transformApiToAnswers = (apiResponse: ApiResponse): { [questionId: string]: string } => {
        return Object.entries(apiResponse).reduce<{ [questionId: string]: string }>((acc, [key, value]) => {
            acc[value.question_id] = value.response;
            return acc;
        }, {});
    };

    const calculateCategoryScores = () => { 
        const newScores: CategoryScores = {}; 
        if (!questions) return; 

        questions.forEach(question => { 
            const category = question.question_category; 
            if (category && !newScores[category]) { 
                newScores[category] = { 
                    name: category, 
                    totalPoints: 0, 
                    questionCount: 0 
                }; 
            } 
        });

        questions.forEach(question => { 
            const category = question.question_category; 
            if (!category || !answers[question.question_order]) return;

            const answeredOption = question.options.find( 
                opt => opt.option_text === answers[question.question_order] 
            );

            if (answeredOption && newScores[category]) { 
                newScores[category].totalPoints += answeredOption.option_weight; 
                newScores[category].questionCount++; 
            } 
        });

        setCategoryScores(newScores);
    };

    const calculateDeviationChart = () => {
        if (!questions || !answers) return;

        const deviationChart: { [key: string]: { positive: number, negative: number } } = {};

        questions.forEach((question) => {
            const category = question.question_category;
            const answer = answers[question.question_order];

            const selectedOption = question.options.find(opt => opt.option_text === answer);
            const optionWeight = selectedOption?.option_weight;

            if (!category || !answer) return;

            if (!deviationChart[category]) {
                deviationChart[category] = { positive: 0, negative: 0 };
            }

            // Count questions instead of using weights
            if (answer === 'Sometimes' || (answer === 'Often' && optionWeight !== 4)) {
                deviationChart[category].positive += 1; // Count Never responses
            } else if (answer === 'Never' || (answer === 'Often' && optionWeight === 4)) {
                deviationChart[category].negative -= 1; // Count Sometimes/Often responses
            }
        });

        const safeData = Object.entries(deviationChart).map(([category, counts]) => ({
            name: category,
            positive: counts.positive || 0,
            negative: counts.negative || 0,
        }));

        setDeviationData(safeData);
    };

    // Median scores for each category as per client requirements
    const categoryMedians: { [key: string]: number } = {
        'Communication': 24,
        'Interpersonal Skills': 18,
        'Problem Solving': 12,
        'Sensory Issue': 18,
        'Hyperactive': 12,
        'Behavioural Issue': 14
    };

    const transformData = (inputData: CategoryScores) => {
        const data = Object.values(inputData).map(item => ({
          name: item.name,
          totalPoints: item.totalPoints,
          scaledQuestionCount: categoryMedians[item.name] || 0
        }));

        return setClusteredBarData(data)
    };

    const actualTotal = Object.values(categoryScores).reduce( 
        (sum, data) => sum + data.totalPoints, 
        0 
    ); 


    const resultRender = (score: number) => {
        if (score >= 49 && score < 98) {
            return {
                category: "No Autism (Neurotypical Development)",
                description: "The score shows that your child has age-appropriate communicational, interpersonal, social interaction and play skills.",
                traits: [
                    "Flexibility and adaptivity to changes in routines and environments.",
                    "Strong range of emotional intelligence and understanding.",
                    "Achieving milestones as expected for their age.",
                    "No further intervention or support needed."
                ]
            };
        } 
        
        if (score >= 98 && score < 123) {
            return {
                category: "Mild Traits (Borderline Autism with Subclinical Traits)",
                description: "The score shows that your child expresses certain subclinical traits in the form of slight social/communication challenges and subtle social cues or occasional struggle with transitions or changes in routine.",
                traits: [
                    "Subclinical traits may disappear as the child ages.",
                    "Can thrive in a mainstream school with early intervention."
                ]
            };
        }
    
        if (score >= 123 && score < 147) {
            return {
                category: "Moderate Autism (Noticeable Autism Spectrum Traits)",
                description: "The score shows that your child exhibits clear challenges in social communication and has repetitive behaviors which can impact day-to-day life.",
                traits: [
                    "Increased sensory needs.",
                    "Struggles with focus and concentration.",
                    "Speech and language delay.",
                    "Requires intervention, therapies, and professional consulting."
                ]
            };
        }
    
        if (score >= 147 && score <= 196) {
            return {
                category: "Definite Autism (Severe Autism or High Support Needs)",
                description: "The score shows that your child has significant deficits in social communication and interaction.",
                traits: [
                    "Minimal or no verbal communication.",
                    "Intense and persistent repetitive behaviors.",
                    "Extreme sensory sensitivities.",
                    "Requires substantial support for daily activities and safety.",
                    "May display self-injurious behaviors or intense meltdowns.",
                    "Requires intervention, therapies, and professional consulting as soon as possible."
                ]
            };
        }
    
        return {
            category: "No score detected",
            description: "The score does not fall into any recognizable category.",
            traits: []
        };
    };    


    const LoadingSpinner = () => {
        return (
            <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center font-sans">
                <div className="bg-white rounded-lg shadow-enterprise-md border border-slate-200/60 p-10 text-center max-w-sm">
                    <div className="relative mb-6">
                        <div className="animate-spin rounded-full h-12 w-12 border-[3px] border-slate-200 border-t-[#0F172A] mx-auto"></div>
                    </div>
                    <h2 className="text-lg font-semibold text-[#0F172A] mb-1 tracking-tight">Loading Assessment</h2>
                    <p className="text-slate-500 text-sm">Preparing your comprehensive report...</p>
                </div>
            </div>
        );
    };

    if (loading){
        return <LoadingSpinner />
    }

    if (error) {
        return (
            <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center font-sans">
                <div className="bg-white rounded-lg shadow-enterprise-md border border-slate-200/60 p-10 text-center max-w-md">
                    <div className="w-14 h-14 bg-red-50 rounded-full mx-auto mb-5 flex items-center justify-center">
                        <FiAlertCircle className="w-7 h-7 text-red-500" />
                    </div>
                    <h2 className="text-lg font-semibold text-[#0F172A] mb-2 tracking-tight">Unable to Load Assessment</h2>
                    <p className="text-slate-500 text-sm mb-6">{error}</p>
                    <button
                        onClick={() => window.location.reload()}
                        className="px-5 py-2.5 bg-[#0F172A] text-white text-sm font-medium rounded-md hover:bg-[#1E293B] transition-colors duration-200"
                    >
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    const getScoreDetails = (score : number) => {
        if (score >= 49 && score < 98) {
          return {
            color: "text-emerald-600",
            bgColor: "bg-emerald-50",
            borderColor: "border-emerald-200",
            label: "Neurotypical",
            badgeClass: "bg-emerald-100 text-emerald-700"
          };
        } else if (score >= 98 && score < 123) {
          return {
            color: "text-amber-600",
            bgColor: "bg-amber-50",
            borderColor: "border-amber-200",
            label: "Mild Traits",
            badgeClass: "bg-amber-100 text-amber-700"
          };
        } else if (score >= 123 && score < 147) {
          return {
            color: "text-orange-600",
            bgColor: "bg-orange-50",
            borderColor: "border-orange-200",
            label: "Moderate",
            badgeClass: "bg-orange-100 text-orange-700"
          };
        } else {
          return {
            color: "text-red-600",
            bgColor: "bg-red-50",
            borderColor: "border-red-200",
            label: "Significant",
            badgeClass: "bg-red-100 text-red-700"
          };
        }
    };

    const scoreDetails = getScoreDetails(actualTotal);

    const CustomTooltip = ({ active, payload }: any) => {
        if (active && payload && payload.length) {
            const { positive, negative, name } = payload[0].payload;

            return (
                <div className="bg-white px-4 py-3 border border-slate-200 rounded-lg shadow-enterprise-md">
                    <p className="font-semibold text-[#0F172A] text-sm mb-2">{name}</p>
                    <div className="space-y-1">
                        <p className="text-xs flex items-center">
                            <span className="w-2 h-2 rounded-full bg-[#DC2626] mr-2"></span>
                            <span className="text-slate-600">Needs attention: <span className="font-medium text-[#DC2626]">{Math.abs(negative)}</span></span>
                        </p>
                        <p className="text-xs flex items-center">
                            <span className="w-2 h-2 rounded-full bg-[#059669] mr-2"></span>
                            <span className="text-slate-600">On track: <span className="font-medium text-[#059669]">{positive}</span></span>
                        </p>
                    </div>
                </div>
            );
        }

        return null;
    };

    const CustomXAxisTick = ({ x, y, payload }: any) => {
        return (
            <g transform={`translate(${x},${y})`}>
                <text
                    x={0}
                    y={0}
                    dy={16}
                    textAnchor="end"
                    fill="#0F172A"
                    fontSize="11"
                    fontWeight="500"
                    fontFamily="Inter, system-ui, sans-serif"
                    transform="rotate(-45)"
                >
                    {payload.value}
                </text>
            </g>
        );
    };

    const CustomLabel = (props: any) => {
        const { x, y, width, value, index } = props;
        const data = clusteredBarData[index];

        if (!data) return null;

        const medianScore = data.scaledQuestionCount;

        return (
            <g>
                <text
                    x={x + width / 2}
                    y={y - 6}
                    fill={value === medianScore ? '#0F172A' : '#0F172A'}
                    textAnchor="middle"
                    fontSize="11"
                    fontWeight="600"
                    fontFamily="Inter, system-ui, sans-serif"
                >
                    {value}
                </text>
            </g>
        );
    };

    return (
        <div className="min-h-screen bg-[#F8FAFC] font-sans antialiased">
            {/* Top Navigation Bar */}
            <div className="bg-white border-b border-slate-200/80 sticky top-0 z-40">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <div className="flex items-center space-x-3">
                            <Logo size="md" />
                            <div className="hidden sm:block h-6 w-px bg-slate-200"></div>
                            <span className="hidden sm:block text-sm font-medium text-slate-600">Assessment Report</span>
                        </div>
                        <div className="flex items-center space-x-2">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${scoreDetails.badgeClass}`}>
                                {scoreDetails.label}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Hero Section */}
                <div className="mb-8">
                    <div className="flex items-center space-x-2 text-sm text-slate-500 mb-2">
                        <HiOutlineDocumentReport className="w-4 h-4" />
                        <span>Autism Spectrum Assessment</span>
                    </div>
                    <h1 className="text-3xl sm:text-4xl font-bold text-[#0F172A] tracking-tight mb-2">
                        Assessment Results
                    </h1>
                    <p className="text-slate-600 text-base sm:text-lg max-w-2xl">
                        A comprehensive analysis of developmental indicators based on your responses.
                    </p>
                </div>

                {/* Score Overview Card */}
                <div className="bg-white rounded-xl border border-slate-200/80 shadow-enterprise-md overflow-hidden mb-6">
                    <div className="p-6 sm:p-8">
                        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
                            {/* Score Display */}
                            <div className="flex-1">
                                <div className="flex items-baseline space-x-3 mb-4">
                                    <span className={`text-5xl sm:text-6xl font-bold tracking-tight ${scoreDetails.color}`}>
                                        {actualTotal}
                                    </span>
                                    <span className="text-slate-400 text-lg font-medium">/ 196</span>
                                </div>
                                <h2 className={`text-xl sm:text-2xl font-semibold mb-3 ${scoreDetails.color}`}>
                                    {resultRender(actualTotal).category}
                                </h2>
                                <p className="text-slate-600 text-sm sm:text-base leading-relaxed max-w-xl">
                                    {resultRender(actualTotal).description}
                                </p>
                            </div>

                            {/* Score Scale */}
                            <div className="lg:w-80">
                                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-3">Score Range</p>
                                <div className="space-y-2">
                                    <div className={`flex items-center justify-between px-3 py-2 rounded-lg transition-all ${actualTotal >= 49 && actualTotal < 98 ? 'bg-emerald-50 border border-emerald-200' : 'bg-slate-50'}`}>
                                        <div className="flex items-center space-x-2">
                                            <div className={`w-2.5 h-2.5 rounded-full ${actualTotal >= 49 && actualTotal < 98 ? 'bg-emerald-500' : 'bg-slate-300'}`}></div>
                                            <span className={`text-sm font-medium ${actualTotal >= 49 && actualTotal < 98 ? 'text-emerald-700' : 'text-slate-500'}`}>Neurotypical</span>
                                        </div>
                                        <span className={`text-xs ${actualTotal >= 49 && actualTotal < 98 ? 'text-emerald-600' : 'text-slate-400'}`}>49-97</span>
                                    </div>
                                    <div className={`flex items-center justify-between px-3 py-2 rounded-lg transition-all ${actualTotal >= 98 && actualTotal < 123 ? 'bg-amber-50 border border-amber-200' : 'bg-slate-50'}`}>
                                        <div className="flex items-center space-x-2">
                                            <div className={`w-2.5 h-2.5 rounded-full ${actualTotal >= 98 && actualTotal < 123 ? 'bg-amber-500' : 'bg-slate-300'}`}></div>
                                            <span className={`text-sm font-medium ${actualTotal >= 98 && actualTotal < 123 ? 'text-amber-700' : 'text-slate-500'}`}>Mild Traits</span>
                                        </div>
                                        <span className={`text-xs ${actualTotal >= 98 && actualTotal < 123 ? 'text-amber-600' : 'text-slate-400'}`}>98-122</span>
                                    </div>
                                    <div className={`flex items-center justify-between px-3 py-2 rounded-lg transition-all ${actualTotal >= 123 && actualTotal < 147 ? 'bg-orange-50 border border-orange-200' : 'bg-slate-50'}`}>
                                        <div className="flex items-center space-x-2">
                                            <div className={`w-2.5 h-2.5 rounded-full ${actualTotal >= 123 && actualTotal < 147 ? 'bg-orange-500' : 'bg-slate-300'}`}></div>
                                            <span className={`text-sm font-medium ${actualTotal >= 123 && actualTotal < 147 ? 'text-orange-700' : 'text-slate-500'}`}>Moderate</span>
                                        </div>
                                        <span className={`text-xs ${actualTotal >= 123 && actualTotal < 147 ? 'text-orange-600' : 'text-slate-400'}`}>123-146</span>
                                    </div>
                                    <div className={`flex items-center justify-between px-3 py-2 rounded-lg transition-all ${actualTotal >= 147 && actualTotal <= 196 ? 'bg-red-50 border border-red-200' : 'bg-slate-50'}`}>
                                        <div className="flex items-center space-x-2">
                                            <div className={`w-2.5 h-2.5 rounded-full ${actualTotal >= 147 && actualTotal <= 196 ? 'bg-red-500' : 'bg-slate-300'}`}></div>
                                            <span className={`text-sm font-medium ${actualTotal >= 147 && actualTotal <= 196 ? 'text-red-700' : 'text-slate-500'}`}>Significant</span>
                                        </div>
                                        <span className={`text-xs ${actualTotal >= 147 && actualTotal <= 196 ? 'text-red-600' : 'text-slate-400'}`}>147-196</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Key Characteristics */}
                <div className="bg-white rounded-xl border border-slate-200/80 shadow-enterprise-md overflow-hidden mb-6">
                    <div className="px-6 py-4 border-b border-slate-100">
                        <div className="flex items-center space-x-2">
                            <HiOutlineClipboardCheck className="w-5 h-5 text-slate-600" />
                            <h2 className="text-lg font-semibold text-[#0F172A]">Key Characteristics</h2>
                        </div>
                    </div>
                    <div className="p-6">
                        <div className="grid gap-3">
                            {resultRender(actualTotal).traits.map((trait, index) => (
                                <div key={index} className="flex items-start space-x-3 p-3 bg-slate-50 rounded-lg">
                                    <FiCheckCircle className="w-5 h-5 text-[#0F172A] mt-0.5 flex-shrink-0" />
                                    <span className="text-sm text-slate-700 leading-relaxed">{trait}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Charts Section */}
                {deviationData.length > 0 && (
                    <div className="grid gap-6 mb-6">
                        {/* Developmental Profile - Modern Card Design */}
                        <div className="bg-white rounded-xl border border-slate-200/80 shadow-enterprise-md overflow-hidden">
                            <div className="px-6 py-5 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h2 className="text-lg font-semibold text-[#0F172A]">Developmental Profile</h2>
                                        <p className="text-sm text-slate-500 mt-0.5">Analysis across key developmental areas</p>
                                    </div>
                                    <div className="hidden sm:flex items-center space-x-4">
                                        <div className="flex items-center space-x-2">
                                            <div className="w-2.5 h-2.5 rounded-full bg-gradient-to-r from-rose-500 to-red-600"></div>
                                            <span className="text-xs text-slate-500 font-medium">Needs Support</span>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <div className="w-2.5 h-2.5 rounded-full bg-gradient-to-r from-emerald-500 to-teal-600"></div>
                                            <span className="text-xs text-slate-500 font-medium">On Track</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="p-4 sm:p-6">
                                {/* Visual Profile with Stacked Bars */}
                                <div className="space-y-4">
                                    {deviationData.map((item, index) => {
                                        const concerns = Math.abs(item.negative);
                                        const strengths = item.positive;
                                        const total = concerns + strengths;
                                        const strengthPercent = total > 0 ? (strengths / total) * 100 : 50;
                                        const concernPercent = total > 0 ? (concerns / total) * 100 : 50;

                                        return (
                                            <div key={index} className="bg-slate-50/50 rounded-xl p-4 border border-slate-100">
                                                {/* Category Name */}
                                                <div className="flex items-center justify-between mb-3">
                                                    <span className="text-sm font-semibold text-[#0F172A]">{item.name}</span>
                                                    <div className="flex items-center space-x-2 text-xs">
                                                        <span className="text-slate-400">Total: {total}</span>
                                                    </div>
                                                </div>

                                                {/* Stacked Bar */}
                                                <div className="relative h-11 bg-slate-200 rounded-xl overflow-hidden flex shadow-inner">
                                                    {/* Concerns (Red) - Left Side */}
                                                    {concerns > 0 && (
                                                        <div
                                                            className="h-full bg-gradient-to-r from-rose-600 to-rose-500 flex items-center transition-all duration-500 relative"
                                                            style={{ width: `${concernPercent}%`, minWidth: concerns > 0 ? '48px' : '0' }}
                                                        >
                                                            <div className="flex items-center justify-center w-full px-2">
                                                                <span className="text-white text-sm font-bold">{concerns}</span>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Strengths (Green) - Right Side */}
                                                    {strengths > 0 && (
                                                        <div
                                                            className="h-full bg-gradient-to-r from-emerald-500 to-emerald-600 flex items-center transition-all duration-500 relative"
                                                            style={{ width: `${strengthPercent}%`, minWidth: strengths > 0 ? '48px' : '0' }}
                                                        >
                                                            <div className="flex items-center justify-center w-full px-2">
                                                                <span className="text-white text-sm font-bold">{strengths}</span>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Labels */}
                                                <div className="flex items-center justify-between mt-2 px-1">
                                                    <span className="text-xs text-rose-600 font-medium">
                                                        {concerns > 0 ? 'Needs attention' : ''}
                                                    </span>
                                                    <span className="text-xs text-emerald-600 font-medium">
                                                        {strengths > 0 ? 'Developing well' : ''}
                                                    </span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* Legend */}
                                <div className="mt-6 flex items-center justify-center space-x-8 py-4 border-t border-slate-100">
                                    <div className="flex items-center space-x-2">
                                        <div className="w-4 h-4 rounded-md bg-gradient-to-r from-rose-600 to-rose-500 shadow-sm"></div>
                                        <span className="text-sm text-slate-600 font-medium">Needs Support</span>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <div className="w-4 h-4 rounded-md bg-gradient-to-r from-emerald-500 to-emerald-600 shadow-sm"></div>
                                        <span className="text-sm text-slate-600 font-medium">On Track</span>
                                    </div>
                                </div>

                                {/* How to Read */}
                                <div className="mt-4 p-4 bg-blue-50 rounded-xl border border-blue-100">
                                    <div className="flex items-start space-x-3">
                                        <FiInfo className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                                        <div>
                                            <p className="text-sm font-medium text-blue-900 mb-1">How to read this chart</p>
                                            <p className="text-xs text-blue-700 leading-relaxed">
                                                Each bar shows the balance between areas needing support (red) and areas developing well (green).
                                                <strong> More green = better progress.</strong> The numbers show how many responses fall into each category.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Score Comparison Chart */}
                        {clusteredBarData && (
                            <div className="bg-white rounded-xl border border-slate-200/80 shadow-enterprise-md overflow-hidden">
                                <div className="px-6 py-4 border-b border-slate-100">
                                    <h2 className="text-lg font-semibold text-[#0F172A]">Score Comparison</h2>
                                    <p className="text-sm text-slate-500 mt-1">Your scores compared to typical median values</p>
                                </div>
                                <div className="p-4 sm:p-6">
                                    {/* Legend at top */}
                                    <div className="flex items-center justify-center space-x-8 mb-4 pb-4 border-b border-slate-100">
                                        <div className="flex items-center space-x-2">
                                            <div className="w-4 h-4 rounded bg-[#0F172A]"></div>
                                            <span className="text-sm text-slate-600 font-medium">Median Score</span>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <div className="w-4 h-4 rounded bg-[#3B82F6]"></div>
                                            <span className="text-sm text-slate-600 font-medium">Your Score</span>
                                        </div>
                                    </div>

                                    {/* Chart - Much taller */}
                                    <div className="h-[520px] sm:h-[560px]">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart
                                                data={clusteredBarData}
                                                margin={{ top: 30, right: 20, left: 10, bottom: 100 }}
                                                barCategoryGap="25%"
                                            >
                                                <XAxis
                                                    dataKey="name"
                                                    height={90}
                                                    interval={0}
                                                    tick={<CustomXAxisTick />}
                                                    axisLine={false}
                                                    tickLine={false}
                                                />
                                                <YAxis
                                                    axisLine={false}
                                                    tickLine={false}
                                                    tick={{ fontSize: 12, fill: '#64748b', fontFamily: 'Inter, system-ui, sans-serif' }}
                                                    domain={[0, 'dataMax + 5']}
                                                    width={35}
                                                />
                                                <Tooltip
                                                    contentStyle={{
                                                        backgroundColor: 'white',
                                                        border: '1px solid #e2e8f0',
                                                        borderRadius: '8px',
                                                        padding: '12px 16px',
                                                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                                                        fontFamily: 'Inter, system-ui, sans-serif'
                                                    }}
                                                    labelStyle={{
                                                        fontWeight: 600,
                                                        color: '#0F172A',
                                                        marginBottom: '8px',
                                                        fontSize: '14px'
                                                    }}
                                                    itemStyle={{
                                                        color: '#64748b',
                                                        fontSize: '13px'
                                                    }}
                                                />
                                                <Bar
                                                    dataKey="scaledQuestionCount"
                                                    fill="#0F172A"
                                                    name="Median Score"
                                                    radius={[6, 6, 0, 0]}
                                                    maxBarSize={60}
                                                    label={<CustomLabel />}
                                                />
                                                <Bar
                                                    dataKey="totalPoints"
                                                    fill="#3B82F6"
                                                    name="Your Score"
                                                    radius={[6, 6, 0, 0]}
                                                    maxBarSize={60}
                                                    label={<CustomLabel />}
                                                />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>

                                    {/* Explanation */}
                                    <div className="mt-6 p-4 bg-blue-50 rounded-xl border border-blue-100">
                                        <div className="flex items-start space-x-3">
                                            <FiInfo className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                                            <div>
                                                <p className="text-sm font-medium text-blue-900 mb-1">How to read this chart</p>
                                                <p className="text-xs text-blue-700 leading-relaxed">
                                                    <strong>Dark bars</strong> show typical median scores. <strong>Blue bars</strong> show your child's scores.
                                                    When the blue bar is shorter than or equal to the dark bar, it indicates better outcomes in that area.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Next Steps Section */}
                <div className="bg-white rounded-xl border border-slate-200/80 shadow-enterprise-md overflow-hidden mb-6">
                    <div className="px-6 py-4 border-b border-slate-100">
                        <div className="flex items-center space-x-2">
                            <HiOutlineLightBulb className="w-5 h-5 text-slate-600" />
                            <h2 className="text-lg font-semibold text-[#0F172A]">Recommended Next Steps</h2>
                        </div>
                    </div>
                    <div className="p-6">
                        {/* Advanced Assessment CTA */}
                        <div className="bg-gradient-to-br from-[#0F172A] to-[#1E293B] rounded-xl p-6 sm:p-8 mb-6 text-white">
                            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                                <div className="flex-1">
                                    <h3 className="text-xl font-semibold mb-2">Advanced Developmental Analysis</h3>
                                    <p className="text-slate-300 text-sm sm:text-base leading-relaxed mb-4">
                                        Get a more comprehensive evaluation with our detailed assessment including speech evaluation, writing skills analysis, and personalized recommendations from certified specialists.
                                    </p>
                                    <ul className="space-y-2 text-sm text-slate-300">
                                        <li className="flex items-center space-x-2">
                                            <FiCheckCircle className="w-4 h-4 text-emerald-400" />
                                            <span>Speech and writing skills evaluation</span>
                                        </li>
                                        <li className="flex items-center space-x-2">
                                            <FiCheckCircle className="w-4 h-4 text-emerald-400" />
                                            <span>Certified specialist assessment</span>
                                        </li>
                                        <li className="flex items-center space-x-2">
                                            <FiCheckCircle className="w-4 h-4 text-emerald-400" />
                                            <span>72-hour flexible completion window</span>
                                        </li>
                                    </ul>
                                </div>
                                <div className="flex-shrink-0">
                                    <a
                                        href={`/advancedsurvey?email=${email}`}
                                        className="inline-flex items-center px-6 py-3 bg-white text-[#0F172A] text-sm font-semibold rounded-lg hover:bg-slate-100 transition-colors duration-200"
                                    >
                                        Start Assessment
                                        <FiArrowRight className="ml-2 w-4 h-4" />
                                    </a>
                                </div>
                            </div>
                        </div>

                        {/* Support Options */}
                        <div className="border-t border-slate-100 pt-6">
                            <p className="text-center text-sm text-slate-600 mb-4">
                                <span className="font-medium">Recommended for children above 3 years.</span> Have questions? Our team is here to help.
                            </p>

                            <div className="flex flex-col sm:flex-row justify-center gap-4">
                                <a
                                    href="mailto:support@shvesa.com"
                                    className="flex items-center justify-center space-x-3 px-5 py-3 border border-slate-200 rounded-lg hover:bg-slate-50 hover:border-slate-300 transition-all duration-200"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <FiMail className="w-5 h-5 text-slate-600" />
                                    <div className="text-left">
                                        <p className="text-sm font-medium text-slate-800">Email Support</p>
                                        <p className="text-xs text-slate-500">support@shvesa.com</p>
                                    </div>
                                </a>

                                <a
                                    href="https://wa.me/916374480026"
                                    className="flex items-center justify-center space-x-3 px-5 py-3 border border-slate-200 rounded-lg hover:bg-emerald-50 hover:border-emerald-200 transition-all duration-200"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <BsWhatsapp className="w-5 h-5 text-emerald-600" />
                                    <div className="text-left">
                                        <p className="text-sm font-medium text-slate-800">WhatsApp</p>
                                        <p className="text-xs text-slate-500">Quick responses</p>
                                    </div>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="text-center py-8 border-t border-slate-200">
                    <div className="flex items-center justify-center mb-3 opacity-70">
                        <Logo size="sm" />
                    </div>
                    <p className="text-slate-500 text-xs">
                        © 2024 Shvesa Minds. Professional autism spectrum assessment platform.
                    </p>
                    <p className="text-slate-400 text-xs mt-1">
                        This assessment is for informational purposes and should not replace professional medical advice.
                    </p>
                </div>
            </div>

            {/* Floating WhatsApp Button */}
            <a
                href="https://wa.me/916374480026"
                className="fixed bottom-6 right-6 z-50 bg-[#25D366] hover:bg-[#20BD5A] text-white rounded-full p-4 shadow-lg hover:shadow-xl transition-all duration-200"
                target="_blank"
                rel="noopener noreferrer"
                title="Chat with us on WhatsApp"
            >
                <svg
                    className="w-6 h-6"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                >
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"></path>
                </svg>
            </a>
        </div>
    ); 
}; 

export default SurveyChart